import { useQueryClient } from '@tanstack/react-query'
import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import { DEFAULT_LOCALE, detectBrowserLocale, isSupportedLocale, type Locale } from '@/i18n/config'
import { changeLocale } from '@/i18n'
import { useAuth } from '@/features/auth/auth-context'
import { queryKeys } from '@/lib/query-client'
import { supabase } from '@/lib/supabase'

interface LocaleContextValue {
  locale: Locale
  setLocale: (locale: Locale) => Promise<void>
  isReady: boolean
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

const STORAGE_KEY = 'legacy.locale'

/**
 * Resolves and persists the interface language.
 *
 * Requirement 6, in precedence order:
 *   1. `profiles.locale` — the user's stored choice, authoritative
 *   2. localStorage — their choice on this device before signing in
 *   3. the browser's Accept-Language list
 *   4. en-AU
 *
 * The database wins over localStorage so that signing in on a borrowed
 * laptop shows your language, not its owner's.
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth()
  const queryClient = useQueryClient()

  const [locale, setLocaleState] = useState<Locale>(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (isSupportedLocale(stored)) return stored
    return detectBrowserLocale()
  })
  const [isReady, setIsReady] = useState(false)

  const apply = useCallback(
    async (next: Locale, persist: boolean) => {
      await changeLocale(next)
      setLocaleState(next)
      localStorage.setItem(STORAGE_KEY, next)

      if (persist && isAuthenticated) {
        // Fire and forget: a failed preference write must never block the
        // interface from switching language.
        const { error } = await supabase.from('profiles').update({ locale: next }).eq('id', user?.id ?? '')
        if (error) console.warn('Could not save language preference', error)
        else void queryClient.invalidateQueries({ queryKey: queryKeys.profile() })
      }
    },
    [isAuthenticated, user?.id, queryClient]
  )

  // Apply the initial locale to i18next before anything renders text.
  // Intentionally runs once; subsequent changes go through `apply`.
  useEffect(() => {
    void changeLocale(locale).then(() => {
      setIsReady(true)
    })
  }, [locale])

  // On sign-in, adopt the stored preference. First-ever login has no
  // stored value, so the browser-detected locale is written back — which
  // is what makes the detection stick across devices.
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return

    // AbortController rather than a `let cancelled` flag: type-aware
    // linting narrows a local boolean to `false` because it cannot see
    // the cleanup closure flip it, and `signal.aborted` sidesteps that
    // while saying what it means.
    const controller = new AbortController()

    void (async () => {
      const { data } = await supabase
        .from('profiles')
        .select('locale')
        .eq('id', user.id)
        .maybeSingle()

      if (controller.signal.aborted) return

      const stored = readLocale(data)

      if (isSupportedLocale(stored)) {
        // Their stored choice wins over this device's localStorage, so
        // signing in on a borrowed laptop shows YOUR language.
        if (stored !== locale) await apply(stored, false)
      } else {
        // First-ever login: persist whatever we detected, so the browser
        // detection sticks across every future device.
        await apply(locale, true)
      }
    })()

    return () => {
      controller.abort()
    }
    // `locale` and `apply` are intentionally excluded: this must run once
    // per sign-in, not on every language change (which `apply` triggers).
  }, [isAuthenticated, user?.id])

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      isReady,
      setLocale: (next: Locale) => apply(next, true),
    }),
    [locale, isReady, apply]
  )

  return <LocaleContext value={value}>{children}</LocaleContext>
}

export function useLocale(): LocaleContextValue {
  const context = use(LocaleContext)
  if (!context) throw new Error('useLocale must be used within a LocaleProvider')
  return context
}

/** Narrow the untyped PostgREST row without an unchecked assertion. */
function readLocale(row: unknown): string | undefined {
  if (typeof row !== 'object' || row === null || !('locale' in row)) return undefined
  return typeof row.locale === 'string' ? row.locale : undefined
}

export { DEFAULT_LOCALE }
